import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db74ep6n6nb9f68e99bg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLen(0);
      return;
    }

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请填写姓名、邮箱和留言内容后再提交。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('请输入有效的邮箱地址。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('留言内容不能超过 500 个字符。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = String(
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? '',
      );

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessageLen(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-mail-send-line text-primary-500"></i>
        给我们留言
      </h2>
      <p className="mt-1.5 text-sm text-foreground-500">
        无论是网站收录申请、内容纠错还是合作咨询，欢迎随时告诉我们。
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="请输入你的姓名"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="用于接收我们的回复，如 name@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            rows={5}
            required
            maxLength={500}
            onChange={(event) => setMessageLen(event.target.value.length)}
            placeholder="请描述你的问题或建议（不超过 500 字）"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          ></textarea>
          <div className="mt-1 text-right text-xs text-foreground-400">{messageLen}/500</div>
        </div>

        <input
          type="text"
          name="website_alt"
          className="aux-form-note"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交留言
            </>
          )}
        </button>
      </div>

      {status === 'success' && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
          <span>提交成功，感谢你的留言！我们会尽快通过邮箱与你联系。</span>
        </div>
      )}

      {status === 'error' && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          <span>{formError}</span>
        </div>
      )}

      <p className="mt-4 text-xs text-foreground-400">
        也可以直接前往
        <Link to="/about" className="mx-1 text-primary-600 hover:text-primary-700">
          关于我们
        </Link>
        了解平台收录标准。
      </p>
    </form>
  );
}